import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesLeft } from "@fortawesome/free-solid-svg-icons/faAnglesLeft";
import { faAngleLeft } from "@fortawesome/free-solid-svg-icons/faAngleLeft";
import { faAnglesRight } from "@fortawesome/free-solid-svg-icons/faAnglesRight";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons/faAngleRight";
import cn from "classnames";
import { ApiExecutorContentInfo } from "../hooks/useApiExecutor";

interface IPaginationProps {
  threshold: number;
  info: ApiExecutorContentInfo;
  showInPage?: number;

  onChange(page: number): Promise<void>;
}

export const PaginationComponent = ({
  info,
  threshold,
  showInPage = 50,
  onChange,
}: IPaginationProps) => {
  if (info == null) {
    return null;
  }

  const currentPageNumber = info.currentPage;
  const totalPagesCount = Math.ceil(info.allContentCounts / showInPage);
  const handleChangePage = (e: React.MouseEvent<HTMLElement>) => {
    const type = e.currentTarget.getAttribute("name");
    if (type === "next") {
      if (currentPageNumber === totalPagesCount) {
        return;
      }
      onChange(currentPageNumber + 1);
    } else if (type === "last") {
      onChange(totalPagesCount);
    } else if (type === "prev") {
      if (currentPageNumber === 1) {
        return;
      }
      onChange(currentPageNumber - 1);
    } else if (type === "first") {
      onChange(1);
    } else {
      if (Number.isNaN(Number(e.currentTarget.innerText))) {
        return;
      }

      onChange(Number(e.currentTarget.innerText));
    }
  };

  const items: any = [];
  const initial =
    currentPageNumber - threshold > 0 ? currentPageNumber - threshold : 1;
  for (let i = initial; i <= currentPageNumber + threshold; i++) {
    if (i > totalPagesCount) {
      break;
    }

    items.push(
      <li
        key={i}
        className={cn(
          "h-full w-full dark:border-gray-500 dark:text-white dark:hover:bg-neutral-700 hover:bg-gray-200 cursor-pointer items-center justify-items-center border-r-2",
          {
            "bg-gray-200": currentPageNumber === i,
          },
        )}
        value={i}
        onClick={handleChangePage}
      >
        <button className={"mr-3 ml-3 h-full text-sm"}>{i}</button>
      </li>,
    );
  }

  return (
    <div>
      {totalPagesCount > 1 && (
        <div className={"flex text-muted text-sm"}>
          <div
            className={
              "h-10 text-xl border-2 border-gray-100 rounded-lg dark:bg-neutral-800 dark:text-white dark:border-gray-500"
            }
          >
            <ul
              className={
                "flex h-full pl-0 dark:text-white border-gray-100 list-none"
              }
            >
              <button
                name={"first"}
                className={
                  "dark:border-gray-500 rounded-l-md dark:hover:bg-neutral-700 hover:bg-gray-200 border-r-2 h-full"
                }
                onClick={handleChangePage}
              >
                <FontAwesomeIcon
                  className={"mx-3 cursor-pointer"}
                  size={"xs"}
                  icon={faAnglesLeft}
                />
              </button>
              <button
                name={"prev"}
                className={
                  "dark:border-gray-500 hover:bg-gray-200 border-r-2 h-full dark:hover:bg-neutral-700"
                }
                onClick={handleChangePage}
              >
                <FontAwesomeIcon
                  className={"ml-3 mr-3 cursor-pointer"}
                  size={"xs"}
                  icon={faAngleLeft}
                />
              </button>
              {currentPageNumber != 1 && currentPageNumber - threshold >= 1 && (
                <li
                  className={
                    "h-full dark:border-gray-500 dark:text-white items-center border-r-2"
                  }
                >
                  <span className={"mr-3 ml-3 h-full"}>...</span>
                </li>
              )}

              {items}

              {currentPageNumber != totalPagesCount &&
                currentPageNumber + threshold < totalPagesCount && (
                  <>
                    <li
                      className={
                        "h-full dark:border-gray-500 dark:text-white items-center border-r-2"
                      }
                    >
                      <span className={"mr-3 ml-3 h-full"}>...</span>
                    </li>
                    <li
                      className={
                        "h-full dark:border-gray-500 hover:bg-gray-200 dark:text-white dark:hover:bg-neutral-700 items-center border-r-2"
                      }
                      onClick={handleChangePage}
                    >
                      <button className={"mr-3 ml-3 h-full"}>
                        {totalPagesCount}
                      </button>
                    </li>
                  </>
                )}

              <button
                name={"next"}
                className={
                  "dark:border-gray-500 hover:bg-gray-200 dark:hover:bg-neutral-700 border-r-2 h-full"
                }
                onClick={handleChangePage}
              >
                <FontAwesomeIcon
                  className={"ml-3 mr-3 cursor-pointer"}
                  size={"xs"}
                  icon={faAngleRight}
                />
              </button>
              <button
                name={"last"}
                className={
                  "rounded-r-md dark:hover:bg-neutral-700 hover:bg-gray-200"
                }
                onClick={handleChangePage}
              >
                <FontAwesomeIcon
                  className={"ml-3 mr-4 cursor-pointer"}
                  size={"xs"}
                  icon={faAnglesRight}
                />
              </button>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
